import React, { useState, useEffect, useCallback } from 'react';
import { baseUrl } from '../../shared';
import { useNavigate } from 'react-router-dom';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListIcon from '@mui/icons-material/List';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArchiveIcon from '@mui/icons-material/Archive';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import VisitModal from '../../components/scheduling/VisitModal';
import VisitDetailModal from '../../components/scheduling/VisitDetailModal';
import ManageReferenceData from '../../components/scheduling/ManageReferenceData';
import { StatusChip } from '../../components/ui';

dayjs.extend(isoWeek);
const localizer = dayjsLocalizer(dayjs);

function CalendarToolbar({ label, date, onNavigate, onView, view }) {
  const navigate = (direction) => {
    if (view === 'agenda') {
      const base = dayjs(date).startOf('month');
      const target = direction === 'PREV'
        ? base.subtract(1, 'month')
        : direction === 'NEXT'
        ? base.add(1, 'month')
        : dayjs().startOf('month');
      onNavigate('DATE', target.toDate());
    } else {
      onNavigate(direction);
    }
  };

  const handleViewChange = (newView) => {
    if (newView === 'agenda') {
      onNavigate('DATE', dayjs(date).startOf('month').toDate());
    }
    onView(newView);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button size="small" variant="outlined" sx={{ minWidth: 36, px: 0.5 }} onClick={() => navigate('PREV')}>
          <ChevronLeftIcon fontSize="small" />
        </Button>
        <Button size="small" variant="outlined" onClick={() => navigate('TODAY')}>Today</Button>
        <Button size="small" variant="outlined" sx={{ minWidth: 36, px: 0.5 }} onClick={() => navigate('NEXT')}>
          <ChevronRightIcon fontSize="small" />
        </Button>
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{dayjs(date).format('MMMM YYYY')}</Typography>
      <ButtonGroup size="small" variant="outlined">
        {['month', 'agenda'].map(v => (
          <Button key={v} variant={view === v ? 'contained' : 'outlined'} onClick={() => handleViewChange(v)}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
}

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const STATUS_LABELS = {
  planned: 'Planned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function AgendaEvent({ event }) {
  const v = event.resource;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: event.color, flexShrink: 0, mt: '4px' }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {v.company}{v.site_locations ? ` — ${v.site_locations}` : ''}
        </Typography>
        {v.work_type_name && (
          <Typography variant="caption" color="text.secondary">{v.work_type_name}</Typography>
        )}
        {v.staff_names && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {v.staff_names}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function MonthEvent({ event }) {
  const v = event.resource;
  const label = v.site_locations ? `${v.company} — ${v.site_locations}` : v.company;
  return (
    <Box sx={{ px: 0.75, overflow: 'hidden' }}>
      <Typography
        variant="caption"
        noWrap
        sx={{ color: '#fff', fontWeight: 600, fontSize: 11, lineHeight: 1.4, display: 'block' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function VisitCard({ visit, onClick, onInspect }) {
  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardActionArea onClick={() => onClick(visit)}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {visit.company} — {visit.site_locations || '—'}
            </Typography>
            <StatusChip domain="visit" value={visit.status} label={STATUS_LABELS[visit.status] || visit.status} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {dayjs(visit.start_date).format('DD/MM/YYYY')} – {dayjs(visit.end_date).format('DD/MM/YYYY')}
            {visit.work_type_name ? ` · ${visit.work_type_name}` : ''}
          </Typography>
          {visit.staff_names && (
            <Typography variant="caption" color="text.secondary">Staff: {visit.staff_names}</Typography>
          )}
          {visit.logistics_names && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Logistics: {visit.logistics_names}
            </Typography>
          )}
          {visit.archivedAt && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
              Archived {dayjs(visit.archivedAt).format('DD/MM/YYYY HH:mm')}{visit.archived_by_name ? ` by ${visit.archived_by_name}` : ''}
            </Typography>
          )}
          {onInspect && visit.asset_count > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Tooltip title="Start visit inspection">
                <IconButton size="small" onClick={e => { e.stopPropagation(); onInspect(visit); }}>
                  <FactCheckIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function Scheduling() {
  const isAdmin = localStorage.getItem('canManageSchedule') === '1';
  const navigate = useNavigate();
  const canInspect = localStorage.getItem('canUseDetailedInspection') === '1' || localStorage.getItem('canInspectAsset') === '1';

  const [view, setView] = useState('calendar');
  const [showArchived, setShowArchived] = useState(false);
  const [listTab, setListTab] = useState(0); // 0=upcoming, 1=past
  const [listSort, setListSort] = useState('asc'); // 'asc'=oldest first, 'desc'=newest first
  const [visits, setVisits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calendarDate, setCalendarDate] = useState(dayjs().startOf('month').toDate());
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editVisitData, setEditVisitData] = useState(null);

  // Filter state (admin only)
  const [filterCompany, setFilterCompany] = useState('');
  const [filterStaff, setFilterStaff] = useState(null);

  const loadVisits = useCallback(() => {
    setLoading(true);
    fetch(baseUrl + 'schedule/visits' + (showArchived ? '?archived=1' : ''), { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setVisits(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [showArchived]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  useEffect(() => {
    if (isAdmin) {
      fetch(baseUrl + 'company/list', { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setCompanies(Array.isArray(d) ? d : []));
      fetch(baseUrl + 'schedule/staff', { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setAllStaff(Array.isArray(d) ? d : []));
    }
  }, [isAdmin]);

  const openDetail = async (visit) => {
    const res = await fetch(baseUrl + 'schedule/visits/' + visit.id, { headers: authHeaders() });
    const data = await res.json();
    setSelectedVisit(data);
    setDetailOpen(true);
  };

  const openEdit = async (visit) => {
    let full = visit;
    if (!visit.staff) {
      const res = await fetch(baseUrl + 'schedule/visits/' + visit.id, { headers: authHeaders() });
      full = await res.json();
    }
    setEditVisitData(full);
    setDetailOpen(false);
    setEditOpen(true);
  };

  const safeStr = s => (s || '').replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

  const handleReport = (visit) => {
    const v = visit || selectedVisit;
    const id = v?.id;
    if (!id) return;
    const pdfDate = v.start_date ? dayjs(v.start_date).format('YYYY-MM-DD') : 'unknown-date';
    const siteStr = Array.isArray(v.sites) ? v.sites.join(', ') : (v.site_locations || '');
    const filename = `${pdfDate}-${safeStr(v.company)}-${safeStr(siteStr || 'no-site')}.pdf`;
    const url = baseUrl + 'schedule/visits/' + id + '/report';
    const headers = authHeaders();
    fetch(url, { headers })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this visit? This cannot be undone.')) return;
    await fetch(baseUrl + 'schedule/visits/' + id, { method: 'DELETE', headers: authHeaders() });
    setDetailOpen(false);
    loadVisits();
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this visit? It can be restored later from the archived view.')) return;
    await fetch(baseUrl + 'schedule/visits/' + id + '/archive', { method: 'PATCH', headers: authHeaders() });
    setDetailOpen(false);
    loadVisits();
  };

  const handleUnarchive = async (id) => {
    await fetch(baseUrl + 'schedule/visits/' + id + '/unarchive', { method: 'PATCH', headers: authHeaders() });
    setDetailOpen(false);
    loadVisits();
  };

  // Apply company + staff filters (admin only)
  const filteredVisits = visits.filter(v => {
    if (filterCompany && v.company !== filterCompany) return false;
    if (filterStaff) {
      const ids = (v.staff_ids || '').split(',').filter(Boolean);
      if (!ids.includes(String(filterStaff.id))) return false;
    }
    return true;
  });

  const companyColorMap = React.useMemo(() => {
    const map = {};
    companies.forEach(c => { if (c.hex_colour) map[c.company] = c.hex_colour; });
    return map;
  }, [companies]);

  const calendarEvents = filteredVisits.map(v => ({
    id: v.id,
    title: `${v.company} – ${v.site_locations || ''}`,
    start: dayjs(v.start_date).toDate(),
    end: dayjs(v.end_date).add(1, 'day').toDate(),
    allDay: true,
    resource: v,
    color: companyColorMap[v.company] || '#1976d2',
  }));

  const today = dayjs().startOf('day');
  const upcoming = filteredVisits
    .filter(v => dayjs(v.end_date).isSameOrAfter(today))
    .sort((a, b) => listSort === 'asc'
      ? dayjs(a.start_date).diff(dayjs(b.start_date))
      : dayjs(b.start_date).diff(dayjs(a.start_date)));
  const past = filteredVisits
    .filter(v => dayjs(v.end_date).isBefore(today))
    .sort((a, b) => listSort === 'asc'
      ? dayjs(a.start_date).diff(dayjs(b.start_date))
      : dayjs(b.start_date).diff(dayjs(a.start_date)));

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      border: 'none',
      borderRadius: 3,
      padding: '1px 0',
      boxShadow: 'none',
    },
  });

  const hasFilters = filterCompany || filterStaff;
  const clearFilters = () => { setFilterCompany(''); setFilterStaff(null); };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Scheduling</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              startIcon={<CalendarMonthIcon />}
              variant={view === 'calendar' ? 'contained' : 'outlined'}
              onClick={() => setView('calendar')}
            >Calendar</Button>
            <Button
              startIcon={<ListIcon />}
              variant={view === 'list' ? 'contained' : 'outlined'}
              onClick={() => setView('list')}
            >List</Button>
          </ButtonGroup>
          {isAdmin && (
            <>
              <Button
                size="small"
                variant={showArchived ? 'contained' : 'outlined'}
                color={showArchived ? 'warning' : 'primary'}
                startIcon={<ArchiveIcon />}
                onClick={() => setShowArchived(s => !s)}
              >
                {showArchived ? 'Viewing Archived' : 'Show Archived'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setEditVisitData(null); setCreateOpen(true); }}
              >
                New Visit
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setSettingsOpen(true)}
                size="small"
              >
                Settings
              </Button>
            </>
          )}
          {canInspect && (
            <Tooltip title="Site Inspection">
              <IconButton onClick={() => navigate('/inspection')}>
                <FactCheckIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Filter bar — admin only */}
      {isAdmin && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Company</InputLabel>
            <Select
              value={filterCompany}
              label="Filter by Company"
              onChange={e => setFilterCompany(e.target.value)}
            >
              <MenuItem value=""><em>All Companies</em></MenuItem>
              {companies.map(c => (
                <MenuItem key={c.company} value={c.company}>{c.company}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={allStaff}
            getOptionLabel={o => o.name + (o.company ? ` (${o.company})` : '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filterStaff}
            onChange={(_, val) => setFilterStaff(val)}
            renderInput={params => <TextField {...params} label="Filter by Staff Member" />}
          />
          {hasFilters && (
            <Button size="small" variant="text" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          {hasFilters && (
            <Typography variant="caption" color="text.secondary">
              Showing {filteredVisits.length} of {visits.length} visits
            </Typography>
          )}
        </Box>
      )}

      {showArchived && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          Viewing archived visits only. Open a visit to restore it.
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Calendar View */}
      {!loading && view === 'calendar' && (
        <Box sx={{
          height: 650, bgcolor: 'var(--surface)', borderRadius: 2, p: 1, border: '1px solid', borderColor: 'divider',
          '& .rbc-agenda-time-cell': { display: 'none' },
          '& .rbc-agenda-table thead tr th:nth-of-type(2)': { display: 'none' },
          // Theme-aware overrides — react-big-calendar's defaults are hard-coded light colours
          // (today = #eaf6ff, off-range cells = #e6e6e6) which look broken in dark mode.
          // Today: a translucent primary tint so it's clearly visible in both modes (the solid
          // --surface-tint token was too subtle on the dark surface to notice).
          '& .rbc-today': { backgroundColor: 'color-mix(in srgb, var(--primary) 22%, transparent)' },
          // Off-range (other-month) days: drop to the darker page background so they read as
          // clearly recessed vs the in-month cells (which sit on --surface). --surface-2 was
          // almost identical to --surface in dark mode, so the months blurred together.
          '& .rbc-off-range-bg': { backgroundColor: 'var(--bg)' },
          '& .rbc-off-range': { color: 'var(--faint)', opacity: 0.7 },
        }}>
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={e => openDetail(e.resource)}
            views={['month', 'agenda']}
            defaultView="month"
            date={calendarDate}
            onNavigate={setCalendarDate}
            length={dayjs(calendarDate).daysInMonth() - 1}
            formats={{ agendaDateFormat: 'ddd DD/MM', agendaHeaderFormat: ({ start }) => dayjs(start).format('MMMM YYYY') }}
            popup
            components={{ toolbar: CalendarToolbar, event: MonthEvent, agenda: { event: AgendaEvent } }}
          />
        </Box>
      )}

      {/* List View */}
      {!loading && view === 'list' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Tabs value={listTab} onChange={(_, v) => setListTab(v)}>
              <Tab label={`Upcoming (${upcoming.length})`} />
              <Tab label="Past" />
            </Tabs>
            <Button
              size="small"
              variant="outlined"
              startIcon={listSort === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              onClick={() => setListSort(s => s === 'asc' ? 'desc' : 'asc')}
            >
              {listSort === 'asc' ? 'Oldest first' : 'Newest first'}
            </Button>
          </Box>

          {listTab === 0 && (
            upcoming.length === 0
              ? <Typography color="text.secondary">No upcoming visits.</Typography>
              : upcoming.map(v => (
                <VisitCard
                  key={v.id}
                  visit={v}
                  onClick={openDetail}
                  onInspect={canInspect ? (v) => navigate('/inspection?visitId=' + v.id) : undefined}
                />
              ))
          )}
          {listTab === 1 && (
            past.length === 0
              ? <Typography color="text.secondary">No past visits.</Typography>
              : past.map(v => (
                <VisitCard
                  key={v.id}
                  visit={v}
                  onClick={openDetail}
                  onInspect={canInspect ? (v) => navigate('/inspection?visitId=' + v.id) : undefined}
                />
              ))
          )}
        </Box>
      )}

      {/* Detail Modal */}
      <VisitDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        visit={selectedVisit}
        onEdit={() => openEdit(selectedVisit)}
        onReport={() => handleReport(selectedVisit)}
        onDelete={isAdmin ? handleDelete : undefined}
        onArchive={isAdmin ? handleArchive : undefined}
        onUnarchive={isAdmin ? handleUnarchive : undefined}
        onInspect={(v) => navigate('/inspection?visitId=' + v.id)}
        onStatusChange={(status) => { setSelectedVisit(v => v ? { ...v, status } : v); loadVisits(); }}
      />

      {/* Create Modal */}
      <VisitModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        visit={null}
        companies={companies}
        onSaved={() => { loadVisits(); setCreateOpen(false); }}
      />

      {/* Edit Modal */}
      <VisitModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        visit={editVisitData}
        companies={companies}
        onSaved={() => { loadVisits(); setEditOpen(false); }}
      />

      {/* Settings */}
      {isAdmin && (
        <ManageReferenceData open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      )}
    </Box>
  );
}
